import { eventCategoryText } from 'constants/eventCategories';
import { dayLabel, fromIsoDay, hourLabel, isoDay, shortDayLabel } from 'helper/timeLabels';

/**
 * What the record pages (a company, a group, a contact) show of their data,
 * in the texts `text` reads (record.*). Pure.
 */

// The badge of where a company stands.
export const STAGE_BADGES = { prospect: 'WARNING', client: 'VALID', lost: 'PRIMARY' };

const wholeEurosFormat = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
// « 12 400 € », its spaces unbreakable: an amount never splits across lines.
export const wholeEuros = (amount) => wholeEurosFormat.format(amount || 0).replace(/\u202f/g, '\u00a0');

// « 123 456 789 00012 »
export const formatSiret = (siret) => {
  const digits = String(siret ?? '').replace(/\D/g, '');
  return digits.length === 14 ? `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}` : digits;
};

export const fullName = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(' ');

// « 8 Nov. 2025 › 15 Nov. 2025 », the period of a mission. Its dates are
// calendar days stored at midnight UTC (or as "2025-11-08"): read in UTC, or
// the West Indies see the day before.
const longDate = (iso) => {
  const date = new Date(iso);
  const month = date.toLocaleDateString('fr-FR', { month: 'short', timeZone: 'UTC' });
  return `${date.getUTCDate()} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${date.getUTCFullYear()}`;
};
export const missionPeriod = (mission) => `${longDate(mission.startsOn)} › ${longDate(mission.endsOn)}`;

/**
 * The three numbers at the top of a company or a group: its missions under
 * way, its revenue of the last 12 months against the 12 before, and what is
 * left to pay. `invoices` is null when the user may not see them, or while
 * they are added up (`invoicesLoading`: those two tiles are `loading`).
 */
export const kpiTiles = ({ current, toFill, invoices, invoicesLoading = false, stage }, text) => {
  const growth = invoices?.revenuePrev ? Math.round(((invoices.revenue - invoices.revenuePrev) / invoices.revenuePrev) * 100) : null;
  const missing = text('kpiNoInvoices');
  let revenueDetail = missing;
  if (invoices) {
    if (growth !== null) revenueDetail = text('kpiGrowth', { growth: `${growth >= 0 ? '+' : ''}${growth}` });
    else if (stage !== 'client') revenueDetail = text('kpiNotClient');
    // Its first year only with revenue now and none before; a client without
    // any for two years (an old one) is not starting.
    else revenueDetail = invoices.revenue ? text('kpiFirstYear') : 'Aucun CA sur 24 mois';
  }
  return [
    {
      key: 'missions',
      label: text('kpiMissions'),
      value: String(current),
      // « Toutes pourvues » only when there are some.
      detail: toFill
        ? text(toFill > 1 ? 'kpiToFillMany' : 'kpiToFillOne', { count: toFill })
        : current
          ? text('kpiAllFilled')
          : 'Aucune mission en cours',
    },
    {
      key: 'revenue',
      label: text('kpiRevenue'),
      loading: invoicesLoading,
      value: invoices ? wholeEuros(invoices.revenue) : '-',
      detail: revenueDetail,
    },
    {
      key: 'unpaid',
      loading: invoicesLoading,
      label: text('kpiUnpaid'),
      value: invoices ? wholeEuros(invoices.unpaidAmount) : '-',
      detail: !invoices
        ? missing
        : invoices.unpaidCount
          ? text(invoices.unpaidCount > 1 ? 'kpiUnpaidMany' : 'kpiUnpaidOne', { count: invoices.unpaidCount })
          : text('kpiNoDelay'),
    },
  ];
};

/**
 * What comes next with a company: its next appointment, task or callback.
 * `contactName`: the person of the callback.
 */
/**
 * The three numbers at the top of a prospect (or a lost company), which has no
 * mission nor invoice: its rendez-vous held and not held, and its calls, over
 * the last 12 months. `results` null (failed): « - ».
 */
export const prospectTiles = ({ results, calls = [], now = new Date() }) => {
  const since = new Date(now);
  since.setFullYear(since.getFullYear() - 1);
  const recent = calls.filter((call) => new Date(call.at) >= since);
  const last = recent.reduce((latest, call) => (!latest || new Date(call.at) > new Date(latest.at) ? call : latest), null);
  const count = (value) => (results ? String(value ?? 0) : '-');
  return [
    { key: 'done', label: 'RDV réalisés', value: count(results?.appointmentsDone), detail: '12 derniers mois' },
    { key: 'notDone', label: 'RDV non réalisés', value: count(results?.appointmentsNotDone), detail: '12 derniers mois' },
    {
      key: 'calls',
      label: 'Appels',
      value: String(recent.length),
      detail: last ? `Dernier le ${shortDayLabel(isoDay(new Date(last.at))).toLowerCase()}` : 'Aucun appel sur 12 mois',
    },
  ];
};

export const nextActionView = (action, { text, contactName, language, now }) => {
  if (!action) return null;
  if (action.kind === 'task') {
    const day = dayLabel(fromIsoDay(action.dueOn));
    return { kind: 'task', title: action.title, detail: action.dueTime ? text('nextWhen', { day, time: action.dueTime.replace(/^0/, '').replace(':', ' h ') }) : day };
  }
  const at = new Date(action.at);
  const when = text('nextWhen', { day: isoDay(at) === isoDay(now) ? text('today') : dayLabel(at), time: hourLabel(at) });
  if (action.kind === 'callback') {
    return { kind: 'callback', title: text('nextCallback', { name: contactName || text('switchboard') }), detail: when };
  }
  return {
    kind: 'appointment',
    title: action.title || eventCategoryText(language, action.category, 'title') || text('nextMeetingAlone'),
    detail: when,
  };
};

/**
 * What comes next with a contact, in the shape of a company's `nextAction`
 * (nextActionView reads both): their earliest open task (late ones first) or
 * their next appointment, from the history of their page; null when nothing
 * is planned.
 */
export const contactNextAction = (history, now) => {
  const taskAt = (task) => fromIsoDay(task.dueOn).getTime() + (task.dueTime ? Number(task.dueTime.slice(0, 2)) * 3600000 + Number(task.dueTime.slice(3, 5)) * 60000 : 0);
  const tasks = (history?.tasks ?? [])
    .filter((task) => !task.doneAt && task.dueOn)
    .map((task) => ({ at: taskAt(task), action: { kind: 'task', id: task.id, title: task.title, dueOn: task.dueOn, dueTime: task.dueTime } }));
  const events = (history?.events ?? [])
    .filter((event) => new Date(event.startsAt) > now)
    .map((event) => ({ at: new Date(event.startsAt).getTime(), action: { kind: 'appointment', id: event.id, at: event.startsAt, title: event.title, category: event.category } }));
  const [first] = [...tasks, ...events].sort((a, b) => a.at - b.at);
  return first?.action ?? null;
};

/**
 * « À venir » and « Derniers échanges » of an overview: the next three and
 * the last three exchanges (the client's own actions stay in the activity).
 */
export const exchangesOf = (items, count = 3) => {
  const exchanges = items.filter((item) => item.kind !== 'client');
  return {
    upcoming: exchanges.filter((item) => item.future).sort((a, b) => a.at - b.at).slice(0, count),
    recent: exchanges.filter((item) => !item.future).sort((a, b) => b.at - a.at).slice(0, count),
  };
};

/**
 * The « Tâches » card of an overview: what is to do (open tasks, call-list
 * entries and appointments to come, the late ones included, so first), what
 * is late, and the tasks done (the latest first).
 */
const PLANNED = ['task', 'event', 'callback'];
export const taskBoardOf = (items) => ({
  todo: items.filter((item) => item.future && PLANNED.includes(item.kind)).sort((a, b) => a.at - b.at),
  late: items.filter((item) => item.late).sort((a, b) => a.at - b.at),
  done: items.filter((item) => item.kind === 'task' && !item.future).sort((a, b) => b.at - a.at),
});

// The date of an exchange in a list: its own words, or its day.
export const exchangeWhen = (item) => item.when ?? shortDayLabel(isoDay(item.at));

